import { useActionState, useRef, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Check, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAccountStore } from '@/stores/account-store'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { OtpInput } from '@/components/otp-input'
import { PasswordInput } from '@/components/password-input'
import { useResendCooldown } from '@/hooks/use-resend-cooldown'
import {
  PASSWORD_MISMATCH_MESSAGE,
  PASSWORD_RULES,
  isPasswordCompliant,
} from '@/services/auth/password-policy'

export function PasswordResetPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const authError = useAccountStore((s) => s.authError)
  const passwordResetSent = useAccountStore((s) => s.passwordResetSent)

  // The login page sends the email along when it redirects a reset-required sign-in.
  const [email, setEmail] = useState((location.state as { email?: string } | null)?.email ?? '')
  const [code, setCode] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const passwordRef = useRef<HTMLInputElement>(null)
  const { cooling, start: startCooldown } = useResendCooldown(5)

  // Same gate as the account password form: the shared rules, then a match.
  const mismatch = confirmPassword.length > 0 && confirmPassword !== newPassword
  const canReset =
    code.length === 6 && isPasswordCompliant(newPassword) && confirmPassword === newPassword

  const [, sendCodeAction, isSendingCode] = useActionState(async () => {
    useAccountStore.getState().clearAuthError()
    try {
      await useAccountStore.getState().sendPasswordResetCode(email)
    } catch {
      // Error handled in store
    }
    return null
  }, null)

  const [, resetAction, isResetting] = useActionState(async () => {
    if (!canReset) return null
    useAccountStore.getState().clearAuthError()
    try {
      await useAccountStore
        .getState()
        .submitPasswordReset({ email, verificationCode: code, newPassword })
      navigate('/login', { replace: true })
    } catch {
      // Error handled in store
    }
    return null
  }, null)

  const resend = async () => {
    startCooldown()
    useAccountStore.getState().clearAuthError()
    try {
      await useAccountStore.getState().sendPasswordResetCode(email)
    } catch {
      // Error handled in store
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <div className="w-full max-w-sm space-y-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground">Reset Password</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {passwordResetSent
              ? 'Enter the code sent to your email'
              : 'Enter your email to receive a reset code'}
          </p>
        </div>

        {authError && (
          <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
            {authError}
          </div>
        )}

        {!passwordResetSent ? (
          <form action={sendCodeAction} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="email" className="text-sm font-medium text-foreground">
                Email
              </label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
              />
            </div>
            <Button type="submit" className="w-full" disabled={isSendingCode}>
              {isSendingCode ? 'Sending...' : 'Send reset code'}
            </Button>
          </form>
        ) : (
          <form action={resetAction} className="space-y-4">
            <div className="space-y-2">
              <span className="text-sm font-medium text-foreground">Verification code</span>
              <OtpInput
                value={code}
                onChange={setCode}
                onComplete={() => passwordRef.current?.focus()}
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="newPassword" className="text-sm font-medium text-foreground">
                New password
              </label>
              <PasswordInput
                id="newPassword"
                ref={passwordRef}
                value={newPassword}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setNewPassword(e.target.value)
                }
                placeholder="New password"
                autoComplete="new-password"
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="confirmPassword" className="text-sm font-medium text-foreground">
                Confirm password
              </label>
              <PasswordInput
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setConfirmPassword(e.target.value)
                }
                placeholder="Confirm password"
                autoComplete="new-password"
                aria-invalid={mismatch || undefined}
                aria-describedby={mismatch ? 'confirmPassword-error' : undefined}
              />
              {mismatch && (
                <p id="confirmPassword-error" className="text-xs text-destructive">
                  {PASSWORD_MISMATCH_MESSAGE}
                </p>
              )}
            </div>
            {newPassword.length > 0 && (
              <ul className="space-y-1" aria-label="Password requirements">
                {PASSWORD_RULES.map((rule) => {
                  const ok = rule.test(newPassword)
                  return (
                    <li
                      key={rule.label}
                      className={cn(
                        'flex items-center gap-2 text-xs',
                        ok ? 'text-emerald-600 dark:text-emerald-500' : 'text-muted-foreground',
                      )}
                    >
                      {ok ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}
                      {rule.label}
                    </li>
                  )
                })}
              </ul>
            )}
            <Button type="submit" className="w-full" disabled={isResetting || !canReset}>
              {isResetting ? 'Resetting...' : 'Reset password'}
            </Button>
            <p className="text-center text-sm text-muted-foreground">
              Didn&apos;t get it?{' '}
              <button
                type="button"
                onClick={resend}
                disabled={cooling || !email}
                className="text-primary hover:underline disabled:opacity-50"
              >
                Resend code
              </button>
            </p>
          </form>
        )}

        <p className="text-center text-sm text-muted-foreground">
          <Link to="/login" className="text-primary hover:underline">
            Back to sign in
          </Link>
        </p>
      </div>
    </div>
  )
}
